import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d63c43qbdoed7v0v8mmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Newsletter submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#060e1a]">
      {/* CTA Banner */}
      <div className="max-w-7xl mx-auto px-8 pt-16">
        <div className="bg-gradient-to-r from-[#0a1628] to-[#0d1f3c] rounded-lg p-12 border border-white/10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#00e5c7]/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#00e5c7]/5 to-transparent"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3
                className="text-2xl font-bold text-white mb-2"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Ready to optimize your tax strategy?
              </h3>
              <p className="text-sm text-white/60">Get started with a free consultation today.</p>
            </div>
            <a
              href="#contact"
              className="px-8 py-4 bg-[#00e5c7] text-[#0a1628] rounded-md text-sm uppercase tracking-widest font-bold hover:bg-[#00ccb0] transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-[#00e5c7]/20"
            >
              Book Consultation
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Column 1 - Brand */}
          <div className="md:col-span-1">
            <img
              src="https://static.readdy.ai/image/8dbd60c8f218d42716666e4ddaa548a8/e10d3fd4f000823b1408934f8a9fe8ab.png"
              alt="AMM Management Group Logo"
              className="h-12 w-auto mb-4"
            />
            <p className="text-sm text-white/50 leading-relaxed mb-6">
              Expert tax strategy and preparation services helping businesses and individuals achieve
              financial success.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex gap-2"
              data-readdy-form
              id="newsletter-form"
            >
              <input
                type="email"
                name="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-md text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00e5c7]/50 transition-colors"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2.5 bg-[#00e5c7] text-[#0a1628] rounded-md hover:bg-[#00ccb0] transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? '...' : 'Subscribe'}
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-[#00e5c7] mt-2">Subscribed successfully!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-400 mt-2">Something went wrong. Please try again.</p>
            )}
          </div>

          {/* Column 2 - Services */}
          <div>
            <h3 className="text-sm font-bold mb-5 text-white uppercase tracking-wider">Services</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="#services" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Tax Strategy Planning
                </a>
              </li>
              <li>
                <a href="#services" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Business Tax Preparation
                </a>
              </li>
              <li>
                <a href="#services" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Individual Tax Filing
                </a>
              </li>
              <li>
                <a href="#services" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  IRS Representation
                </a>
              </li>
              <li>
                <a href="#services" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Estate Planning
                </a>
              </li>
              <li>
                <a href="#services" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Quarterly Reviews
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3 - Company */}
          <div>
            <h3 className="text-sm font-bold mb-5 text-white uppercase tracking-wider">Company</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="#" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  About Us
                </a>
              </li>
              <li>
                <a href="#" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Our Team
                </a>
              </li>
              <li>
                <a href="#" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Careers
                </a>
              </li>
              <li>
                <a href="#resources" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Blog
                </a>
              </li>
              <li>
                <a href="#contact" className="text-white/50 hover:text-[#00e5c7] transition-colors cursor-pointer">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 - Office Hours */}
          <div>
            <h3 className="text-sm font-bold mb-5 text-white uppercase tracking-wider">Office Hours</h3>
            <ul className="space-y-3 text-sm text-white/50 mb-6">
              <li className="flex justify-between">
                <span>Monday - Friday</span>
                <span>9:00 AM - 6:00 PM</span>
              </li>
              <li className="flex justify-between">
                <span>Saturday</span>
                <span>10:00 AM - 2:00 PM</span>
              </li>
              <li className="flex justify-between">
                <span>Sunday</span>
                <span>Closed</span>
              </li>
            </ul>
            <div className="flex gap-3">
              <div className="w-14 h-14 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
                <span className="text-xs font-bold text-[#00e5c7]">CPA</span>
              </div>
              <div className="w-14 h-14 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center">
                <span className="text-xs font-bold text-[#00e5c7]">IRS</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/40">
            <div>{'©'} 2024 AMM Management Group. All rights reserved.</div>
            <div className="flex gap-6">
              <a href="#" className="hover:text-[#00e5c7] transition-colors cursor-pointer">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-[#00e5c7] transition-colors cursor-pointer">
                Terms of Service
              </a>
              <a href="#" className="hover:text-[#00e5c7] transition-colors cursor-pointer">
                Accessibility
              </a>
            </div>
            <div>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#00e5c7] transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
